// SPDX-License-Identifier: GPL-3.0-only
import { CommonModule } from '@angular/common';
import { Component, OnDestroy } from '@angular/core';
import { FormsModule } from '@angular/forms';
import { firstValueFrom, Subject, takeUntil } from 'rxjs';

import { AccountService } from '@bitwarden/common/auth/abstractions/account.service';
import { UserId } from '@bitwarden/common/types/guid';
import { SyncService } from '@bitwarden/common/vault/abstractions/sync/sync.service.abstraction';
import { ImportServiceAbstraction } from '../services';

import * as parser from './vbango-parser';
import { LocalDraft, FieldName, assertPreserved, insertionFits } from './vbango-draft';

@Component({
  selector: 'vbango-organizer',
  templateUrl: './vbango-organizer.component.html',
  imports: [CommonModule, FormsModule],
})
export class VbangoOrganizerComponent implements OnDestroy {
  draft = new LocalDraft(parser);
  source = '';
  showSource = true;
  filename = '';
  format = 'auto';
  error = '';
  showPassword = new Set<object>();
  userId: UserId | null = null;
  private destroy$ = new Subject<void>();
  private fileGeneration = 0;
  private alive = true;

  constructor(
    private importService: ImportServiceAbstraction,
    private accountService: AccountService,
    private syncService: SyncService,
  ) {
    accountService.activeAccount$.pipe(takeUntil(this.destroy$)).subscribe((account) => {
      const userId = account?.id ?? null;
      if (this.userId !== userId) this.clear(true);
      this.userId = userId;
    });
  }

  sourceChanged(): void {
    this.fileGeneration++;
    this.filename = '';
    this.draft.clear();
    this.showPassword.clear();
    this.error = this.source.length > parser.MAX_TEXT_CHARS ? '内容超出 250,000 字符，请分批处理；本次不会截断或保存。' : '';
  }

  checkPaste(event: ClipboardEvent): void {
    const input = event.target as HTMLTextAreaElement;
    const inserted = event.clipboardData?.getData('text/plain');
    if (inserted != null && !insertionFits(this.source, inserted, input.selectionStart, input.selectionEnd, parser.MAX_TEXT_CHARS)) {
      event.preventDefault();
      this.error = '整段粘贴超过 250,000 字符，本次没有插入任何内容，请分批处理。';
    }
  }

  async readFile(event: Event): Promise<void> {
    const input = event.target as HTMLInputElement;
    const file = input.files?.[0];
    this.clear();
    const generation = this.fileGeneration;
    input.value = '';
    if (!file) return;
    if (file.size > parser.MAX_FILE_BYTES || !/\.(txt|csv|tsv)$/i.test(file.name)) {
      this.error = '请选择不超过 1 MiB 的 UTF-8 TXT、CSV 或 TSV 文件。';
      return;
    }
    try {
      const raw = await file.arrayBuffer();
      if (!this.alive || generation !== this.fileGeneration) return;
      // Even malformed/oversized content must require explicit reveal after a file read.
      this.showSource = false;
      this.source = new TextDecoder('utf-8', { fatal: true }).decode(raw);
      this.filename = file.name;
      this.organize();
    } catch (_) {
      if (this.alive && generation === this.fileGeneration) this.error = '文件读取失败或不是有效 UTF-8；请检查原文件。';
    }
  }

  organize(): void {
    try {
      this.draft.organize(this.source, this.format, this.filename, this.userId);
      this.showSource = false;
      this.error = '';
    } catch (_) { this.error = '资料未能整理，请检查格式、数量与长度；没有丢弃或截断内容。'; }
  }

  edit(index: number, key: FieldName, event: Event): void {
    this.draft.records[index][key] = (event.target as HTMLInputElement).value;
    this.draft.edited();
  }

  toggle(record: object): void {
    this.showPassword.has(record) ? this.showPassword.delete(record) : this.showPassword.add(record);
  }

  delete(index: number): void {
    const record = this.draft.records[index];
    this.showPassword.delete(record);
    record.extraFields.forEach((field) => this.showPassword.delete(field));
    this.draft.records.splice(index, 1);
    this.draft.edited();
  }

  async save(): Promise<void> {
    this.error = '';
    const expectedOwner = this.userId;
    try {
      const saved = await this.draft.save(this.userId, async (json, expectedId) => {
        const active = await firstValueFrom(this.accountService.activeAccount$);
        if (!this.alive || active?.id !== expectedId) throw new Error('Account changed');
        const importer = this.importService.getImporter('bitwardenjson', async () => {
          throw new Error('No file password accepted for local draft');
        }, null);
        if (!importer) throw new Error('Pinned native importer unavailable');
        const nativeParse = importer.parse.bind(importer);
        importer.parse = async (contents) => {
          const result = await nativeParse(contents);
          assertPreserved(contents, result);
          const current = await firstValueFrom(this.accountService.activeAccount$);
          if (!this.alive || current?.id !== expectedId) throw new Error('Account changed');
          return result;
        };
        return this.importService.import(importer, json, null, null, false, expectedId as UserId);
      });
      if (saved && this.alive && this.userId === expectedOwner) {
        this.source = '';
        this.showSource = true;
        this.filename = '';
        this.showPassword.clear();
        const current = await firstValueFrom(this.accountService.activeAccount$);
        if (!this.alive || current?.id !== expectedOwner) return;
        try { await this.syncService.fullSync(true); }
        catch (_) {
          if (this.alive && this.userId === expectedOwner && this.draft.phase === 'saved') {
            this.draft.message += ' 同步刷新未完成，可稍后刷新；不要再次导入。';
          }
        }
      }
    } catch (_) { this.error = '请先重新整理、核对资料并确认使用同一已解锁账户。'; }
  }

  clear(force = false): void {
    this.fileGeneration++;
    this.source = '';
    this.showSource = true;
    this.filename = '';
    this.error = '';
    this.showPassword.clear();
    this.draft.clear(force);
  }

  ngOnDestroy(): void {
    this.alive = false;
    this.clear(true);
    this.destroy$.next();
    this.destroy$.complete();
  }
}
